import { HostAriaLabelMixin, SchmancyFormField } from '@schmancy/mixins/index'
import { componentText } from '@schmancy/mixins/component-text'
import type { InputSize, SchmancyInput } from '@schmancy/form/fields/input'
import '@schmancy/form/fields/input/input'
import '@schmancy/primitives/button/button'
import '@schmancy/primitives/icons/icon'
import '@schmancy/primitives/option/option'
import type SchmancyOption from '@schmancy/primitives/option/option'
import { show } from '@schmancy/overlay/overlay.service'
import { html, render as renderTemplate, unsafeCSS } from 'lit'
import { customElement, property, queryAssignedElements, state } from 'lit/decorators.js'
import { createRef, ref } from 'lit/directives/ref.js'
import { ifDefined } from 'lit/directives/if-defined.js'
import {
	BehaviorSubject,
	Subject,
	Observable,
	combineLatest,
	defer,
	debounceTime,
	exhaustMap,
	finalize,
	fromEvent,
	merge,
	switchMap,
	takeUntil,
	tap,
} from 'rxjs'
import { similarity } from '@schmancy/utils/search'
import style from './autocomplete.scss?inline'

export type SchmancyAutocompleteChangeEvent = CustomEvent<{ value: string | string[]; values?: string[] }>

function searchText(value: string): string {
	return value
		.normalize('NFD')
		.replace(/\p{Diacritic}/gu, '')
		.toLocaleLowerCase()
		.trim()
}

/** A committed option value with an independent searchable overlay. */
@customElement('schmancy-autocomplete')
export default class SchmancyAutocomplete extends HostAriaLabelMixin(SchmancyFormField(unsafeCSS(style))) {
	valueSet: boolean = false
	valuesSet: boolean = false

	// Autocomplete-specific properties only — `name`, `label`, `required`,
	// `error`, `validationMessage` come from the mixin.
	@property({ type: String }) placeholder = ''
	@property({ type: String }) maxHeight = '300px'
	@property({ type: Boolean }) multi = false
	@property({ type: String }) description = ''
	@property({ type: String, reflect: true }) size: InputSize = 'md'
	@property({ type: String }) autocomplete = 'off'
	@property({ type: Number }) debounceMs = 200
	@property({ type: Number }) similarityThreshold = 0.3 // Minimum similarity score to show option

	/**
	 * Replaces the whole accessible name of the remove control on every chip
	 * this field mints in `multi` mode, which is `Remove` when unset. Named for
	 * the part it reaches — the chip — because the field has a remove control
	 * nowhere else; the name is replaced whole rather than composed from a
	 * translated verb, for the reason the chip's own `removeLabel` states.
	 */
	@property({ type: String, attribute: 'chip-remove-label' }) chipRemoveLabel = ''

	@property({ type: Array })
	get values() {
		return [...this.selectedValues$.value]
	}
	set values(vals: string[]) {
		this.valuesSet = true
		this.selectedValues$.next(Array.isArray(vals) ? [...vals] : [])
	}

	// Value property — narrowed override of the mixin's wide value union.
	// The `?.`/early-return guards survive the mixin's `value = ''` field
	// initializer, which fires during the superclass construction phase —
	// before these subjects exist (mirrors select.ts).
	@property({ type: String, reflect: true })
	override get value(): string {
		if (this.multi) return this.selectedValues$?.value.join(',') ?? ''
		return this.selectedValue$?.value ?? ''
	}
	override set value(val: string) {
		if (!this.selectedValue$ || !this.selectedValues$) return
		this.valueSet = true
		if (this.multi) {
			const newValues = val
				? val
						.split(',')
						.map(v => v.trim())
						.filter(Boolean)
				: []
			const currentValues = this.selectedValues$.value
			if (JSON.stringify(newValues) !== JSON.stringify(currentValues)) {
				this.selectedValues$.next(newValues)
			}
		} else {
			if (val !== this.selectedValue$.value) {
				this.selectedValue$.next(val)
				this.requestUpdate()
			}
		}
	}

	@state() private open = false
	@queryAssignedElements({ flatten: true }) private options!: SchmancyOption[]
	private inputElementRef = createRef<SchmancyInput>()
	private searchRef = createRef<SchmancyInput>()
	private selectedValue$ = new BehaviorSubject<string>('')
	private selectedValues$ = new BehaviorSubject<string[]>([])
	private query$ = new BehaviorSubject('')
	private optionsChanged$ = new Subject<void>()
	private openRequested$ = new Subject<void>()
	private closeRequested$ = new Subject<void>()
	private panel?: HTMLDivElement
	private draft: string[] = []
	private activeIndex = -1

	override connectedCallback() {
		super.connectedCallback()
		merge(
			combineLatest([this.selectedValue$, this.selectedValues$]).pipe(
				tap(() => {
					for (const option of this.options ?? []) {
						option.selected = this.multi ? this.values.includes(option.value) : option.value === this.value
					}
					this.internals?.setFormValue(this.value || null)
					this.checkValidity()
					this.requestUpdate()
					this.renderPicker()
				}),
			),
			this.query$.pipe(
				tap(() => {
					this.activeIndex = -1
				}),
				debounceTime(this.debounceMs),
				tap(() => this.renderPicker()),
			),
			new Observable<void>(observer => {
				const mutations = new MutationObserver(() => observer.next())
				mutations.observe(this, {
					childList: true,
					subtree: true,
					characterData: true,
					attributes: true,
					attributeFilter: ['value', 'label', 'disabled'],
				})
				return () => mutations.disconnect()
			}).pipe(tap(() => this.optionsChanged$.next())),
			this.optionsChanged$.pipe(
				tap(() => {
					this.requestUpdate()
					this.renderPicker()
				}),
				switchMap(() =>
					merge(
						...(this.options ?? []).map(option =>
							fromEvent<CustomEvent>(option, 'option-select').pipe(tap(() => this.choose(option))),
						),
					),
				),
			),
			this.openRequested$.pipe(
				exhaustMap(() =>
					defer(() => {
						if (this.disabled || this.readonly) return new Observable<void>(observer => observer.complete())
						this.draft = this.values
						this.panel = document.createElement('div')
						this.panel.className = 'w-80 max-w-full p-2'
						this.panel.style.width = 'min(20rem, calc(100vw - 2rem))'
						this.open = true
						this.query$.next('')
						this.renderPicker()
						return show(this.panel).pipe(
							takeUntil(this.closeRequested$),
							finalize(() => {
								this.open = false
								if (this.panel) renderTemplate(null, this.panel)
								this.panel = undefined
								if (this.inputElementRef.value) this.inputElementRef.value.ariaControlsElements = []
								if (this.isConnected) {
									const custom = this.shadowRoot
										?.querySelector<HTMLSlotElement>('slot[name=trigger]')
										?.assignedElements()[0]
									if (custom instanceof HTMLElement) custom.focus()
									else this.inputElementRef.value?.focus()
								}
							}),
						)
					}),
				),
			),
		)
			.pipe(takeUntil(this.disconnecting))
			.subscribe()
		if (this.hasUpdated) this.optionsChanged$.next()
	}

	override firstUpdated(changed: Map<PropertyKey, unknown>) {
		super.firstUpdated(changed)
		this.optionsChanged$.next()
	}

	override updated(changed: Map<PropertyKey, unknown>) {
		super.updated(changed)
		if ((this.disabled || this.readonly) && this.open) this.closeRequested$.next()
		this.renderPicker()
	}

	override resetForm() {
		this.closeRequested$.next()
		this.selectedValue$.next('')
		this.selectedValues$.next([])
		super.resetForm()
	}

	private visibleOptions() {
		const term = searchText(this.query$.value)
		return (this.options ?? [])
			.map(option => {
				const label = searchText(option.label || option.textContent || '')
				const code = searchText(option.value)
				const score = !term
					? 1
					: label.includes(term) || code.includes(term)
						? 2
						: Math.max(similarity(term, label) * 1.1, similarity(term, code))
				const selected = this.multi ? this.draft.includes(option.value) : option.value === this.value
				return { option, score, selected }
			})
			.filter(item => !term || item.score >= this.similarityThreshold)
			.sort((a, b) => (term ? b.score - a.score : Number(b.selected) - Number(a.selected)))
	}

	private choose(option: SchmancyOption) {
		if (this.disabled || this.readonly || option.disabled) return
		if (this.multi && this.open) {
			this.draft = this.draft.includes(option.value)
				? this.draft.filter(value => value !== option.value)
				: [...this.draft, option.value]
			this.renderPicker()
			return
		}
		if (this.multi)
			this.selectedValues$.next(
				this.values.includes(option.value)
					? this.values.filter(value => value !== option.value)
					: [...this.values, option.value],
			)
		else this.selectedValue$.next(option.value)
		this.fireChangeEvent()
		this.closeRequested$.next()
	}

	private fireChangeEvent() {
		this.markTouched()
		this.dispatchEvent(
			new CustomEvent<SchmancyAutocompleteChangeEvent['detail']>('change', {
				detail: { value: this.value, ...(this.multi ? { values: this.values } : {}) },
				bubbles: true,
				composed: true,
			}),
		)
	}

	public checkValidity(): boolean {
		if (this.required && !(this.multi ? this.values.length : Boolean(this.value)))
			return this.setFieldValidity(
				{ valueMissing: true },
				this.errorMessages?.valueMissing ?? componentText('autocomplete.required'),
			)
		return this.setFieldValidity({})
	}

	public reportValidity(): boolean {
		const valid = this.checkValidity()
		if (!valid) {
			this.markTouched()
			this.inputElementRef.value?.focus()
		}
		return valid
	}

	private handlePickerKey(event: KeyboardEvent) {
		if (event.key === 'Escape') {
			event.preventDefault()
			event.stopPropagation()
			this.closeRequested$.next()
			return
		}
		const options = this.visibleOptions().filter(item => !item.option.disabled)
		if (['ArrowDown', 'ArrowUp', 'Home', 'End'].includes(event.key)) {
			event.preventDefault()
			if (event.key === 'Home') this.activeIndex = 0
			else if (event.key === 'End') this.activeIndex = options.length - 1
			else
				this.activeIndex = (this.activeIndex + (event.key === 'ArrowDown' ? 1 : -1) + options.length) % options.length
			this.renderPicker()
			this.panel?.querySelector<HTMLElement>(`[data-option-index="${this.activeIndex}"]`)?.focus()
		} else if (event.key === 'Enter') {
			event.preventDefault()
			event.stopPropagation()
			const option = options[this.activeIndex < 0 ? 0 : this.activeIndex]?.option
			if (option) this.choose(option)
		}
	}

	private renderPicker() {
		if (!this.panel) return
		const options = this.visibleOptions()
		let index = -1
		renderTemplate(
			html`
				<div class="flex items-center gap-2 pb-2" @keydown=${(event: KeyboardEvent) => this.handlePickerKey(event)}>
					<schmancy-input
						${ref(this.searchRef)}
						class="min-w-0 flex-1"
						.placeholder=${componentText('autocomplete.search')}
						.ariaLabel=${componentText('autocomplete.search')}
						.value=${this.query$.value}
						@schmancy:field:connect=${(event: Event) => event.stopPropagation()}
						@change=${(event: Event) => event.stopPropagation()}
						@input=${(event: CustomEvent<{ value: string }>) => {
							event.stopPropagation()
							this.query$.next(event.detail.value)
						}}
					></schmancy-input>
					<schmancy-button compact color="neutral" @click=${() => this.closeRequested$.next()}>
						<schmancy-icon slot="prefix">close</schmancy-icon>
						${componentText('autocomplete.cancel')}
					</schmancy-button>
				</div>
				<div
					role="listbox"
					id=${`${this.id}-options`}
					aria-label=${this.ariaLabel || this.label || this.placeholder}
					aria-multiselectable=${this.multi ? 'true' : 'false'}
					class="overflow-y-auto flex flex-col"
					style="max-height: ${this.maxHeight};"
					@keydown=${(event: KeyboardEvent) => this.handlePickerKey(event)}
				>
					${options.map(({ option, selected }) => {
						if (!option.disabled) index++
						const rowIndex = option.disabled ? -1 : index
						return html` <schmancy-option
							.value=${option.value}
							.label=${option.label || option.textContent || ''}
							aria-label=${option.label || option.textContent || ''}
							.selected=${selected}
							.disabled=${option.disabled}
							.icon=${option.icon}
							.group=${option.group}
							tabindex="-1"
							data-option-index=${rowIndex}
							class=${rowIndex >= 0 && rowIndex === this.activeIndex ? 'ring-1 ring-primary-default' : ''}
							@option-select=${() => this.choose(option)}
						></schmancy-option>`
					})}
					${options.length === 0 ? html`<div role="status" class="p-4 text-sm text-surface-onVariant">${componentText('autocomplete.empty', { query: this.query$.value })}</div>` : ''}
				</div>
				${
					this.multi
						? html`<div class="flex justify-end pt-2">
								<schmancy-button
									@click=${() => {
										this.selectedValues$.next([...this.draft])
										this.fireChangeEvent()
										this.closeRequested$.next()
									}}
									>${componentText('autocomplete.apply')}</schmancy-button
								>
							</div>`
						: ''
				}
			`,
			this.panel,
		)
		const listbox = this.panel.querySelector<HTMLElement>('[role=listbox]')
		if (listbox && this.inputElementRef.value) this.inputElementRef.value.ariaControlsElements = [listbox]
		void this.searchRef.value?.updateComplete.then(() => {
			if (this.panel && !this.panel.matches(':focus-within')) this.searchRef.value?.focus()
		})
	}

	override render() {
		const selected = (this.options ?? []).filter(option =>
			this.multi ? this.values.includes(option.value) : option.value === this.value,
		)
		return html`<div
			class="w-full"
			@keydown=${(event: KeyboardEvent) => {
				if (['Enter', ' ', 'ArrowDown'].includes(event.key)) {
					event.preventDefault()
					event.stopPropagation()
					this.openRequested$.next()
				}
			}}
		>
			${this.description ? html`<span id=${`${this.id}-description`} class="sr-only">${this.description}</span>` : ''}
			<slot name="trigger" @click=${() => this.openRequested$.next()}>
				<schmancy-input
					${ref(this.inputElementRef)}
					class="w-full"
					.size=${this.size}
					.label=${this.label}
					.placeholder=${this.placeholder}
					.value=${selected.map(option => option.label || option.textContent || '').join(', ')}
					.ariaLabel=${this.ariaLabel || this.label || this.placeholder}
					.readonly=${true}
					.required=${this.required}
					autocomplete=${this.autocomplete}
					.disabled=${this.disabled}
					.error=${this.error}
					.validationMessage=${this.validationMessage}
					.hint=${this.hint}
					role="combobox"
					aria-haspopup="listbox"
					aria-expanded=${this.open ? 'true' : 'false'}
					aria-describedby=${ifDefined(this.description ? `${this.id}-description` : undefined)}
					@schmancy:field:connect=${(event: Event) => event.stopPropagation()}
					@change=${(event: Event) => event.stopPropagation()}
				></schmancy-input>
			</slot>
			<div hidden><slot @slotchange=${() => this.optionsChanged$.next()}></slot></div>
		</div>`
	}
}

declare global {
	interface HTMLElementTagNameMap {
		'schmancy-autocomplete': SchmancyAutocomplete
	}
}
